import {useQuery} from "@apollo/client";
import {useEffect, useRef} from "react";
import {Helmet} from "react-helmet-async";
import {FaLongArrowAltRight} from "react-icons/fa";
import {Link, Navigate, useParams, useSearchParams} from "react-router";

import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {GlobalPingBanner} from "@/components/globalPingBanner/GlobalPingBanner.tsx";
import ContentContainer from "@/components/layout/ContentContainer.tsx";
import {UpErrorBoundary} from "@/lib/handlers/UpErrorBoundary.tsx";
import {getListTitle} from "@/lib/hooks/useTable.utils.ts";
import {FindCheckResponse, GQL_EDIT_CHECK_DATA} from "@/lib/queries/checks.ts";
import {CheckTypeDisplay} from "@/lib/types/checks.ts";
import {LimitCheckerComponent} from "@/pages/billing/v2/InstantCheckout/LimitCheckWithInstantCheckout.tsx";
import {LimitRumPageViewsLimit} from "@/pages/billing/v2/InstantCheckout/LimitRumPageViewsLimit.tsx";
import {ChecksActionBar} from "@/pages/checks/list/actions/ChecksActionBar.tsx";
import {getLegacyPopRedirectPath, normalizeLegacyChecksListParams} from "@/pages/checks/list/Checks.utils.ts";
import {ChecksList} from "@/pages/checks/list/components/ChecksList.tsx";

export const Checks = () => {
  const {groupId} = useParams();
  const [searchParams] = useSearchParams();
  const {user} = useCurrentUserContext();

  const pop = searchParams.get("pop") || "";
  const popRedirectPath = getLegacyPopRedirectPath(pop);

  // A query, not a fetch in an effect: Apollo keeps only the open Group's result, so a slow earlier one cannot land.
  const {data: groupData} = useQuery<FindCheckResponse>(GQL_EDIT_CHECK_DATA, {
    variables: {id: Number(groupId)},
    skip: !groupId,
    fetchPolicy: "no-cache",
  });

  const rumPageViewsLimitCheckRef = useRef<LimitCheckerComponent>();
  useEffect(() => {
    if (!groupId && !parseInt(pop) && !user?.account?.free_trial_expires_at) {
      rumPageViewsLimitCheckRef.current?.checkIfLimitHit();
    }
  });

  // One navigation at most: `pop` leaves the page in place of the link, so Back skips it, and legacy keys are rewritten
  // before ChecksList mounts so useTable reads the current keys on its first render.
  if (popRedirectPath) {
    return <Navigate replace to={popRedirectPath} />;
  }
  const legacyRedirectSearch = normalizeLegacyChecksListParams(searchParams)?.toString();
  if (legacyRedirectSearch !== undefined) {
    return <Navigate replace to={{search: legacyRedirectSearch}} />;
  }

  const group = groupData?.findCheck;
  // Until the name loads, the heading still says this is a Group: the list heading would claim every Check.
  const title = groupId ? (
    <Link to={`/checks/${groupId}`} className="d-block text-primary fw-bold">
      {group ? `Group: ${group.name}` : "Group"} <FaLongArrowAltRight className={"align-middle"} />
    </Link>
  ) : (
    getListTitle(searchParams, "Checks", {key: "monitoring_service_type", display: CheckTypeDisplay})
  );

  return (
    <ContentContainer title={title} banner={<GlobalPingBanner />} actionsBar={<ChecksActionBar />}>
      <Helmet>
        <title>Checks</title>
      </Helmet>
      <UpErrorBoundary>
        <LimitRumPageViewsLimit ref={rumPageViewsLimitCheckRef} />
        {/* Keyed so a different Group loads its own stored filters instead of the URL sync clearing them. */}
        <ChecksList key={groupId} groupId={groupId} />
      </UpErrorBoundary>
    </ContentContainer>
  );
};
